import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Blog } from "@/components/Blog";
import { usePageSeo } from "@/hooks/usePageSeo";

const BlogPage = () => {
  const { language, url, onLanguageChange } = usePageSeo();

  const content = {
    es: {
      title: "Blog de Marketing Digital e IA - Guías Profesionales | Luis ROC",
      description: "Descubre estrategias avanzadas de IA para marketing digital, cómo conquistar el mercado estadounidense y automatizar tu negocio. Guías paso a paso por experto en marketing hispano.",
      keywords: "marketing digital, inteligencia artificial, IA marketing, mercado estadounidense, automatización marketing, ChatGPT marketing, SEO IA, Google Ads IA",
      url: "https://luisroc.com/blog",
      breadcrumbs: [
        { name: "Inicio", url: "https://luisroc.com" },
        { name: "Blog", url: "https://luisroc.com/blog" }
      ]
    },
    en: {
      title: "Digital Marketing & AI Blog - Professional Guides | Luis ROC",
      description: "Discover advanced AI strategies for digital marketing, how to conquer the American market and automate your business. Step-by-step guides by Hispanic marketing expert.",
      keywords: "digital marketing, artificial intelligence, AI marketing, American market, marketing automation, ChatGPT marketing, AI SEO, Google Ads AI",
      url: "https://luisroc.com/en/blog",
      breadcrumbs: [
        { name: "Home", url: "https://luisroc.com/en" },
        { name: "Blog", url: "https://luisroc.com/en/blog" }
      ]
    }
  };

  const t = content[language];

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>{t.title}</title>
        <meta name="description" content={t.description} />
        <meta name="keywords" content={t.keywords} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="language" content={language === 'es' ? 'Spanish' : 'English'} />
        <meta name="geo.region" content="US" />
        <meta name="geo.country" content="US" />
        <meta name="geo.placename" content="United States" />
        
        <link rel="canonical" href={url} />
        
        {/* Open Graph */}
        <meta property="og:title" content={t.title} />
        <meta property="og:description" content={t.description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="ROC Worldwide Agency LLC" />
        <meta property="og:locale" content={language === 'es' ? 'es_US' : 'en_US'} />
        <meta property="og:image" content="https://luisroc.com/media/luis-roc/portrait.png" />
        <meta property="og:image:alt" content={language === 'es' ? 'Blog de Marketing Digital e IA por Luis ROC' : 'Digital Marketing & AI Blog by Luis ROC'} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@luisrocmarketing" />
        <meta name="twitter:creator" content="@luisrocmarketing" />
        <meta name="twitter:title" content={t.title} />
        <meta name="twitter:description" content={t.description} />
        <meta name="twitter:image" content="https://luisroc.com/media/luis-roc/portrait.png" />
        <meta name="twitter:image:alt" content={language === 'es' ? 'Blog de Marketing Digital e IA por Luis ROC' : 'Digital Marketing & AI Blog by Luis ROC'} />
        
        {/* Hreflang */}
        
        {/* Preconnect for performance */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        
        {/* Structured Data - Organization & Website */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Blog",
                "@id": t.url + "#blog",
                "mainEntityOfPage": {
                  "@type": "WebPage",
                  "@id": t.url
                },
                "name": t.title,
                "description": t.description,
                "url": t.url,
                "inLanguage": language === 'es' ? 'es-US' : 'en-US',
                "author": { "@id": "https://luisroc.com/#luisroc" },
                "publisher": {
                  "@id": "https://luisroc.com/#organization"
                },
                "keywords": t.keywords,
                "genre": ["Digital Marketing", "Artificial Intelligence", "Business Strategy"],
                "audience": {
                  "@type": "Audience",
                  "audienceType": "Business Owners, Marketing Professionals, Entrepreneurs"
                }
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": t.breadcrumbs.map((crumb, index) => ({
                  "@type": "ListItem",
                  "position": index + 1,
                  "name": crumb.name,
                  "item": crumb.url
                }))
              }
            ]
          })}
        </script>
      </Helmet>
      
      <Header language={language} onLanguageChange={onLanguageChange} />
      <main>
        <Blog language={language} />
      </main>
      <Footer language={language} />
    </>
  );
};

export default BlogPage;